import { useEffect, useState } from "react";
import { Link } from "react-router";
import { AlertTriangle, FileText, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SearchInput } from "@/components/ui/search-input";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { Combobox } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import { ListPagination } from "@/components/ui/list-pagination";
import { FreelancersEPeriodos } from "@/components/finance/FreelancersEPeriodos";
import { useInternalLink } from "@/hooks/use-internal-link";
import { useAgencias } from "@/hooks/use-agencias";
import { useOmieCompetencias } from "@/hooks/use-finance-omie";
import { usePropostas } from "@/hooks/use-finance-propostas";
import { formatDateTime } from "@/utils/dateUtils";
import { formatCents, parseMoeda } from "@/utils/money";
import { getPropostaStatusInfo, PROPOSTA_STATUS_LIST } from "@/utils/proposta-status";
import type { PropostaResumo, PropostaStatus } from "@/types/finance";

const PAGE_SIZE = 24;
const STATUS_TODOS = "todos";
const COMPETENCIA_TODAS = "todas";

interface PropostasTabProps {
  /** Quando presente (aba "Propostas" da ficha da agência), trava o filtro e esconde o seletor. */
  fixedAgenciaId?: string;
}

export function PropostasTab({ fixedAgenciaId }: PropostasTabProps) {
  const [status, setStatus] = useState<PropostaStatus | typeof STATUS_TODOS>(STATUS_TODOS);
  const [competencia, setCompetencia] = useState<string>(COMPETENCIA_TODAS);
  const [agenciaId, setAgenciaId] = useState(fixedAgenciaId ?? "");
  const [termoBusca, setTermoBusca] = useState("");
  const [termoDebounced, setTermoDebounced] = useState("");
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTermoDebounced(termoBusca);
      setPagina(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [termoBusca]);

  const { data: agencias = [] } = useAgencias();
  const competenciasQuery = useOmieCompetencias();

  const query = usePropostas({
    agencia_id: agenciaId || undefined,
    status: status === STATUS_TODOS ? undefined : status,
    competencia: competencia === COMPETENCIA_TODAS ? undefined : competencia,
    q: termoDebounced || undefined,
    page: pagina,
    page_size: PAGE_SIZE,
  });

  const itens = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const opcoesAgencia = [
    { value: "", label: "Todas as agências" },
    ...agencias.map((agencia) => ({ value: agencia.id, label: agencia.nome })),
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button asChild className="gap-1.5">
          <Link to={`/financeiro/propostas/nova${fixedAgenciaId ? `?agencia=${fixedAgenciaId}` : ""}`}>
            <Plus className="h-4 w-4" />
            Nova proposta
          </Link>
        </Button>
      </div>

      <FilterToolbar>
        <SearchInput
          value={termoBusca}
          onChange={setTermoBusca}
          placeholder="Buscar por código, agência ou profissional"
        />
        <Select
          value={status}
          onValueChange={(valor) => {
            setStatus(valor as PropostaStatus | typeof STATUS_TODOS);
            setPagina(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={STATUS_TODOS}>Todos os status</SelectItem>
            {PROPOSTA_STATUS_LIST.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={competencia}
          onValueChange={(valor) => {
            setCompetencia(valor);
            setPagina(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={COMPETENCIA_TODAS}>Todas as competências</SelectItem>
            {(competenciasQuery.data?.items ?? []).map((item) => (
              <SelectItem key={item.competencia} value={item.competencia}>
                {item.competencia}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!fixedAgenciaId && (
          <Combobox
            options={opcoesAgencia}
            value={agenciaId}
            onValueChange={(valor) => {
              setAgenciaId(valor);
              setPagina(1);
            }}
            allowCustomValue={false}
            placeholder="Todas as agências"
            className="w-full sm:w-[220px]"
          />
        )}
      </FilterToolbar>

      {query.isLoading ? (
        <LoadingState variant="list" count={6} />
      ) : query.isError ? (
        <ErrorState onRetry={() => query.refetch()} />
      ) : itens.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Nenhuma proposta encontrada"
          description="Ajuste os filtros ou crie uma nova proposta."
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Freelancers / períodos</TableHead>
                  {!fixedAgenciaId && <TableHead>Agência</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead>Competência</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Pendências</TableHead>
                  <TableHead>Atualizada em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itens.map((proposta) => (
                  <LinhaProposta
                    key={proposta.id}
                    proposta={proposta}
                    mostrarAgencia={!fixedAgenciaId}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
          <ListPagination currentPage={pagina} totalPages={totalPaginas} onPageChange={setPagina} />
        </>
      )}
    </div>
  );
}

function LinhaProposta({
  proposta,
  mostrarAgencia,
}: {
  proposta: PropostaResumo;
  mostrarAgencia: boolean;
}) {
  const linkProps = useInternalLink();
  const href = `/financeiro/propostas/${proposta.id}`;
  const statusInfo = getPropostaStatusInfo(proposta.status);

  return (
    <TableRow {...linkProps(href)} className="cursor-pointer hover:bg-muted/50">
      <TableCell className="whitespace-nowrap text-xs font-medium">{proposta.codigo ?? "Sem código"}</TableCell>
      <TableCell className="min-w-44 text-sm">
        <FreelancersEPeriodos periodos={proposta.periodos} />
      </TableCell>
      {mostrarAgencia && (
        <TableCell>
          <Link
            to={`/agencias/${proposta.agencia.id}`}
            onClick={(e) => e.stopPropagation()}
            className="hover:underline"
          >
            {proposta.agencia.nome}
          </Link>
        </TableCell>
      )}
      <TableCell>
        <Badge variant="outline" className={statusInfo.badgeClass}>
          {statusInfo.label}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{proposta.competencia ?? "-"}</TableCell>
      <TableCell className="text-right font-medium">
        {formatCents(proposta.totais.total_cents, parseMoeda(proposta.moeda))}
      </TableCell>
      <TableCell className="text-sm">
        {proposta.documento_atual ? (
          <div className="space-y-0.5">
            <span>v{proposta.documento_atual.versao}</span>
            {proposta.documento_atual.pdf_pendente && (
              <Badge variant="outline" className="ml-1.5 border-amber-200 bg-amber-50 text-amber-700">
                PDF pendente
              </Badge>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">Sem documento</span>
        )}
      </TableCell>
      <TableCell>
        {proposta.pendencias_bloqueantes > 0 ? (
          <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-700">
            <AlertTriangle className="h-3 w-3" />
            {proposta.pendencias_bloqueantes}
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
        {formatDateTime(proposta.updated_at)}
      </TableCell>
    </TableRow>
  );
}
